import itineraryApi, { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useFormatCurrencyForItinerary from '@/src/hooks/currency/useFormatCurrencyForItinerary'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import useRedirectToSrp from '@/src/hooks/navigation/useRedirectToSrp'
import { useAppDispatch } from '@/src/redux/store'
import { getAirlineSummary } from '@/src/utils/itinerary/priceChange'
import { useTranslations } from 'next-intl'
import PriceChangePopup from './priceChangePopup'

const PriceChangePopupWrapper = ({ triggerPrepaymentOnly }: { triggerPrepaymentOnly?: () => void }) => {
    const itineraryId = useGetItineraryId()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const t = useTranslations('common')
    const redirectToSrp = useRedirectToSrp()
    const dispatch = useAppDispatch()

    const { metaData, flights = [] } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data }) => {
                return {
                    metaData: data?.metaData,
                    flights: data?.flights
                }
            }
        }
    )

    const { priceChangePopupData } = metaData || {}
    const {
        isPriceChange,
        priceChangePopupShown,
        oldPrice = 0,
        newPrice = 0,
        callPrePaymentOnAcceptPriceChange
    } = priceChangePopupData || {}

    const acceptPriceChange = () => {
        if (callPrePaymentOnAcceptPriceChange) {
            triggerPrepaymentOnly?.()
        }
        dispatch(
            itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                draft.metaData.priceChangePopupData = {
                    ...draft.metaData.priceChangePopupData,
                    priceChangePopupShown: true,
                    callPrePaymentOnAcceptPriceChange: false
                }
            })
        )
    }

    const { isMultiAirline, airlineName } = getAirlineSummary(flights)
    const airlineNameText = isMultiAirline || !airlineName ? t('airline') : airlineName

    const isPriceIncreased = newPrice > oldPrice
    const priceDifference = Math.abs(newPrice - oldPrice)
    const title = isPriceIncreased
        ? t('priceIncreaseHeading', { airlineName: airlineNameText, priceDifference: formatCurrency(priceDifference) })
        : t('priceDecreaseHeading', { airlineName: airlineNameText, priceDifference: formatCurrency(priceDifference) })
    const subHeading = isPriceIncreased
        ? t('priceIncreaseSubheading', { oldPrice: formatCurrency(oldPrice), newPrice: formatCurrency(newPrice) })
        : t('priceDecreaseSubheading', { newPrice: formatCurrency(newPrice) })

    const primaryButtonText = isPriceIncreased ? t('selectDifferentFlight') : t('bookAtThisPrice')
    const primaryActionClick = isPriceIncreased ? redirectToSrp : acceptPriceChange
    const secondaryButtonText = isPriceIncreased ? t('bookAtThisPrice') : ''
    const secondaryActionClick = isPriceIncreased ? acceptPriceChange : () => {}

    if (!isPriceChange || priceChangePopupShown || (!callPrePaymentOnAcceptPriceChange && priceDifference <= 1))
        return <></>
    return (
        <PriceChangePopup
            title={title}
            subHeading={subHeading}
            primaryButtonText={primaryButtonText}
            primaryButtonAction={primaryActionClick}
            secondaryButtonText={secondaryButtonText}
            secondaryButtonAction={secondaryActionClick}
            onClose={acceptPriceChange}
        />
    )
}

export default PriceChangePopupWrapper
